import { useState, useEffect } from 'react';
import { Settings, HardDrive, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useAuthStore } from '@/stores/auth-store';
import { db } from '@/services/storage/db';
import { Button } from '@/components/common/Button';
import { StorageUsageIndicator } from '@/components/photos/StorageUsageIndicator';
import { DeleteAccountSheet } from '@/components/profile/DeleteAccountSheet';
import { deleteAccount } from '@/services/account-service';
import {
  requestPermission,
  subscribe,
  unsubscribe,
  getSubscription,
  saveSubscriptionToSupabase,
  removeSubscriptionFromSupabase,
  getSubscriptionCount,
} from '@/services/push-subscription-service';

const US_STATES = [
  { value: '', label: 'Select a state' },
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

type Theme = 'light' | 'dark' | 'system';

const THEME_OPTIONS: { value: Theme; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-[var(--shadow-card)] p-4 sm:p-5">
      <h2
        data-variant="field-label"
        className="field-label mb-4"
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

function FieldLabel({ htmlFor, children }: { htmlFor?: string; children: React.ReactNode }) {
  return (
    <label
      htmlFor={htmlFor}
      className="block field-label mb-1"
    >
      {children}
    </label>
  );
}

export function ProfilePage() {
  const navigate = useNavigate();
  const { user, preferences, updatePreferences, signOut } = useAuthStore();

  const [showSignOutDialog, setShowSignOutDialog] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const [showDeleteAccountSheet, setShowDeleteAccountSheet] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState<string | null>(null);

  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [isSavingName, setIsSavingName] = useState(false);
  const [nameSaved, setNameSaved] = useState(false);

  // Notification state
  const [browserPermission, setBrowserPermission] = useState<NotificationPermission | 'unsupported'>('default');
  const [subscriptionCount, setSubscriptionCount] = useState(0);
  const [isTogglingNotifications, setIsTogglingNotifications] = useState(false);
  const [notificationError, setNotificationError] = useState<string | null>(null);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as Window & { MSStream?: unknown }).MSStream;
  const isPWAInstalled = window.matchMedia('(display-mode: standalone)').matches;

  useEffect(() => {
    if (!('Notification' in window)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- early-return branch clears state before async path
      setBrowserPermission('unsupported');
      return;
    }
    setBrowserPermission(Notification.permission);
    if (preferences?.notificationsEnabled) {
      void getSubscriptionCount().then(setSubscriptionCount);
    }
  }, [preferences?.notificationsEnabled]);

  async function handleSaveDisplayName() {
    if (!user || displayName.trim() === user.displayName) return;
    setIsSavingName(true);
    try {
      const updated = await db.updateUser(user.id, { displayName: displayName.trim() });
      useAuthStore.setState({ user: updated });
      setNameSaved(true);
      setTimeout(() => setNameSaved(false), 2000);
    } finally {
      setIsSavingName(false);
    }
  }

  async function handleThemeChange(theme: Theme) {
    await updatePreferences({ theme });
  }

  async function handleStateChange(e: React.ChangeEvent<HTMLSelectElement>) {
    if (!user) return;
    try {
      const updated = await db.updateUser(user.id, { defaultState: e.target.value });
      useAuthStore.setState({ user: updated });
    } catch (err) {
      console.error('[ProfilePage] Failed to save default state:', err);
    }
  }

  async function handleNotificationsToggle() {
    if (isTogglingNotifications) return;
    setIsTogglingNotifications(true);
    setNotificationError(null);
    const enabling = !(preferences?.notificationsEnabled ?? false);

    try {
      if (enabling) {
        // 1. Request browser permission
        const permission = await requestPermission();
        setBrowserPermission(permission);
        if (permission !== 'granted') {
          // 'denied': the browserPermission banner already explains; no extra error needed.
          // 'default': user dismissed the prompt.
          if (permission !== 'denied') {
            setNotificationError('Permission was not granted. Try again to enable notifications.');
          }
          return;
        }
        // 2. Subscribe to Web Push
        const sub = await subscribe();
        if (!sub) {
          setNotificationError('Push subscriptions are not supported in this browser.');
          return;
        }
        // 3. Persist subscription to Supabase
        await saveSubscriptionToSupabase(sub);
        // 4. Update preferences
        await updatePreferences({ notificationsEnabled: true });
        const count = await getSubscriptionCount();
        setSubscriptionCount(count);
      } else {
        // Disable: unsubscribe this device and remove from Supabase
        const existing = await getSubscription();
        if (existing) {
          await removeSubscriptionFromSupabase(existing.endpoint);
          await unsubscribe();
        }
        await updatePreferences({ notificationsEnabled: false });
        setSubscriptionCount(0);
      }
    } catch (err) {
      console.error('[ProfilePage] Notification toggle failed:', err);
      setNotificationError('Something went wrong. Please try again.');
    } finally {
      setIsTogglingNotifications(false);
    }
  }

  async function handleLeadDaysChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = parseInt(e.target.value, 10);
    if (!isNaN(val) && val >= 1 && val <= 14) {
      await updatePreferences({ notificationLeadDays: val });
    }
  }

  async function handleDeleteAccountConfirm() {
    setIsDeletingAccount(true);
    setDeleteAccountError(null);
    try {
      await deleteAccount(navigate);
    } catch (err) {
      console.error('[ProfilePage] Account deletion failed:', err);
      setDeleteAccountError(
        err instanceof Error ? err.message : 'Account deletion failed. Please try again.',
      );
    } finally {
      setIsDeletingAccount(false);
    }
  }

  const currentTheme = preferences?.theme ?? 'dark';
  const notificationsEnabled = preferences?.notificationsEnabled ?? false;
  const notificationLeadDays = preferences?.notificationLeadDays ?? 3;

  const inputBase =
    'w-full bg-[var(--color-elevated)] border border-[var(--color-border)] rounded text-[var(--color-text-primary)] text-body-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] transition-colors duration-100';

  const selectBase = inputBase;

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Go back"
          data-testid="back-button"
          className="flex items-center justify-center -ml-1 p-1 rounded text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
        >
          <ChevronLeft size={20} />
        </button>
        <Settings size={18} className="text-[var(--color-text-muted)]" />
        <h1 className="font-display text-display-sm text-[var(--color-text-display)]">Profile &amp; Settings</h1>
      </div>

      {/* Account */}
      <SectionCard title="Account">
        <div className="space-y-4">
          <div>
            <FieldLabel htmlFor="displayName">Display name</FieldLabel>
            <div className="flex gap-2">
              <input
                id="displayName"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className={inputBase + ' flex-1'}
                placeholder="Your name"
              />
              <Button
                type="button"
                onClick={handleSaveDisplayName}
                disabled={isSavingName || displayName.trim() === (user?.displayName ?? '')}
                className="shrink-0"
              >
                {nameSaved ? 'Saved!' : 'Save'}
              </Button>
            </div>
          </div>

          <div>
            <FieldLabel>Email</FieldLabel>
            <p className="text-body-sm text-[var(--color-text-secondary)] py-2 px-3 bg-[var(--color-bg)] border border-[var(--color-border-subtle)] rounded">
              {user?.email ?? '—'}
            </p>
          </div>

          <div className="pt-1 border-t border-[var(--color-border)] mt-2 flex flex-col gap-3">
            <Button
              type="button"
              variant="danger"
              onClick={() => setShowSignOutDialog(true)}
            >
              Sign Out
            </Button>

            {/* Delete Account — M72 Phase 3 */}
            <Button
              type="button"
              variant="danger"
              onClick={() => {
                setDeleteAccountError(null);
                setShowDeleteAccountSheet(true);
              }}
              data-testid="delete-account-open-button"
            >
              Delete Account
            </Button>
          </div>
        </div>
      </SectionCard>

      {/* Preferences */}
      <SectionCard title="Preferences">
        <div className="space-y-5">
          {/* Theme picker */}
          <div>
            <FieldLabel>Theme</FieldLabel>
            <div
              role="radiogroup"
              aria-label="Theme"
              className="flex gap-1 bg-[var(--color-bg)] rounded-lg p-1"
            >
              {THEME_OPTIONS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={currentTheme === value}
                  onClick={() => void handleThemeChange(value)}
                  className={`flex-1 py-1.5 rounded-md text-body-sm font-medium transition-colors duration-100 ${
                    currentTheme === value
                      ? 'bg-[var(--color-accent)] text-white'
                      : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Default state */}
          <div>
            <FieldLabel htmlFor="defaultState">Default state</FieldLabel>
            <select
              id="defaultState"
              value={user?.defaultState ?? ''}
              onChange={handleStateChange}
              className={selectBase}
            >
              {US_STATES.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

        </div>
      </SectionCard>

      {/* M-07 (Defect Batch M): Default Property section removed.
          Primary property is now managed inline on PropertyDetailPage via
          the "Set as Primary" toggle, and surfaced as a "Primary" badge
          on PropertiesPage cards. */}

      {/* Data storage notice */}
      <div className="flex items-start gap-3 px-4 py-3 rounded-lg bg-[var(--color-elevated)] border border-[var(--color-border-subtle)]">
        <HardDrive size={16} className="text-[var(--color-text-muted)] mt-0.5 shrink-0" />
        <div className="flex flex-col gap-1">
          <p className="text-body-xs text-[var(--color-text-muted)] leading-relaxed">
            Your data is stored locally and backed up to the cloud when online.
          </p>
          {user && <StorageUsageIndicator userId={user.id} showBar />}
        </div>
      </div>

      {/* Sign out confirmation dialog */}
      {showSignOutDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowSignOutDialog(false)}
          />
          <div className="relative w-full max-w-sm bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl p-6 shadow-[var(--shadow-card-md)]">
            <h2 className="font-display text-display-sm text-[var(--color-text-display)] mb-2">
              Sign out of Ridgen?
            </h2>
            <p className="text-body-sm text-[var(--color-text-muted)] mb-6">
              You&apos;ll need to sign in again. Your data is saved to your account.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowSignOutDialog(false)}
                disabled={isSigningOut}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="danger"
                disabled={isSigningOut}
                onClick={async () => {
                  setIsSigningOut(true);
                  try {
                    await signOut();
                  } finally {
                    setIsSigningOut(false);
                    setShowSignOutDialog(false);
                  }
                }}
              >
                {isSigningOut ? 'Signing out…' : 'Sign Out'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Sheet — M72 Phase 3 */}
      {showDeleteAccountSheet && (
        <DeleteAccountSheet
          isDeleting={isDeletingAccount}
          error={deleteAccountError}
          onConfirm={() => void handleDeleteAccountConfirm()}
          onClose={() => {
            if (!isDeletingAccount) {
              setShowDeleteAccountSheet(false);
              setDeleteAccountError(null);
            }
          }}
        />
      )}

      {/* Notification preferences */}
      <SectionCard title="Notifications">
        <div className="space-y-4">
          {/* iOS home screen caveat */}
          {isIOS && !isPWAInstalled && (
            <p className="text-body-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded px-3 py-2">
              On iPhone, notifications require adding Ridgen to your home screen first (Share → Add to Home Screen).
            </p>
          )}

          {/* Browser doesn't support Notifications */}
          {browserPermission === 'unsupported' && (
            <p className="text-body-xs text-[var(--color-text-muted)]">
              This browser doesn't support push notifications.
            </p>
          )}

          {/* Browser permission denied */}
          {browserPermission === 'denied' && (
            <p className="text-body-xs text-red-500 bg-red-500/10 rounded px-3 py-2">
              Notifications are blocked. Enable them in your browser or system settings, then try again.
            </p>
          )}

          {/* Error from toggle attempt */}
          {notificationError && (
            <p className="text-body-xs text-red-500 bg-red-500/10 rounded px-3 py-2">
              {notificationError}
            </p>
          )}

          <div className="flex items-center justify-between">
            <div>
              <p className="text-body-sm text-[var(--color-text-primary)] font-medium">Enable notifications</p>
              <p className="text-body-xs text-[var(--color-text-muted)]">
                {notificationsEnabled && subscriptionCount > 0
                  ? `Active on ${subscriptionCount} device${subscriptionCount === 1 ? '' : 's'}`
                  : 'Get reminders before tasks are due'}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-label="Enable notifications"
              aria-checked={notificationsEnabled}
              disabled={isTogglingNotifications || browserPermission === 'unsupported'}
              onClick={() => void handleNotificationsToggle()}
              // M-08 (Defect Batch M): inward outline so focus ring is never clipped (ADR-035).
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-2 focus:outline-[var(--color-accent)] focus:outline-offset-[-2px] disabled:opacity-40 ${
                notificationsEnabled ? 'bg-[var(--color-accent)]' : 'bg-[var(--color-elevated)]'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ${
                  notificationsEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div>
            <FieldLabel htmlFor="leadDays">Notify me this many days before a task is due</FieldLabel>
            <input
              id="leadDays"
              type="number"
              min={1}
              max={14}
              value={notificationLeadDays}
              onChange={handleLeadDaysChange}
              disabled={!notificationsEnabled}
              className={inputBase + ' w-24 disabled:opacity-40'}
            />
            <p className="mt-1 text-body-xs text-[var(--color-text-muted)]">1–14 days</p>
          </div>
        </div>
      </SectionCard>
    </div>
  );
}
